import {
  LeaderboardType,
  TLeaderboard,
  TLeaders,
} from '@chancer/common/lib/interfaces/firestore/FirestoreInterfaces';
import {
  TLocalLeaderboard,
  getLocalLeaderboardImageFileName,
  getLocalLeaderboardPrizeImageFileName,
} from '@chancer/common/lib/interfaces/firestore/LocalJsonInterfaces';
import {convert} from '@chancer/common/lib/utils/FirebaseUtils';
import {assertType} from '@chancer/common/lib/utils/TypeUtils';
import firebase from 'firebase/compat/app';
import {fileSearch, uploadAssetForDocument} from './CompetitionUploadUtils';

export const uploadAndConfigureLeaderboards = async (
  config: {protocol: string; cdnDomain: string},
  compId: string,
  vendor: string,
  files: File[],
  leaderboardConfigurations: TLocalLeaderboard[],
  prefix: string = '',
  log: (message: string) => void,
): Promise<string[]> => {
  const leaderboardIds: string[] = [];
  //TODO: Intentionally storing and returning the inGameLeaderboard will not be be required when we remove the legacy leaderboard from the game document
  let inGameLeaderboard: TLeaderboard | null = null;

  for (let i = 0; i < leaderboardConfigurations.length; i++) {
    const leaderboardConfig = leaderboardConfigurations[i];
    let leaderboardRef: firebase.firestore.DocumentReference<firebase.firestore.DocumentData>;
    let leaderboardToUpload: TLeaderboard;

    if (leaderboardConfig.type === LeaderboardType.INGAME) {
      log(
        `InGame Leaderboard [type: ${leaderboardConfig.type}] [vendor: ${vendor}] [key: ${leaderboardConfig.key}].\n`,
      );
      leaderboardRef = firebase
        .firestore()
        .collection('leaderboards')
        .doc(compId);
      leaderboardToUpload = {
        ...leaderboardConfig,
        vendor,
        comps: [compId],
      };
      inGameLeaderboard = leaderboardToUpload;
    } else {
      const leaderboardQuerySnap = await firebase
        .firestore()
        .collection('leaderboards')
        .where('vendor', '==', vendor)
        .where('key', '==', leaderboardConfig.key)
        .get();
      const isNew = leaderboardQuerySnap.empty;
      if (leaderboardQuerySnap.size > 1) {
        throw new Error(
          'There appears to be more than one leaderboard with this vendor / key combination, this should never happen. Please contact a Chancer developer',
        );
      }
      if (isNew) {
        log(
          `New Leaderboard [type: ${leaderboardConfig.type}] [vendor: ${vendor}] [key: ${leaderboardConfig.key}].\n`,
        );
        leaderboardRef = firebase.firestore().collection('leaderboards').doc();
        leaderboardToUpload = {
          ...leaderboardConfig,
          vendor,
          comps: [compId],
        };
        // Write before uploading files if this is a new leaderboard so
        // that we don't lose the id
        await leaderboardRef.set(leaderboardToUpload);
      } else {
        log(
          `Updating existing Leaderboard [type: ${leaderboardConfig.type}] [vendor: ${vendor}] [key: ${leaderboardConfig.key}].\n`,
        );
        leaderboardRef = leaderboardQuerySnap.docs[0].ref;
        const leaderboardDoc = await leaderboardRef.get();
        const existingLeaderboard = convert(TLeaderboard, leaderboardDoc);
        leaderboardToUpload = {
          ...existingLeaderboard,
          ...leaderboardConfig,
          comps: existingLeaderboard.comps
            .filter((v) => v !== compId)
            .concat(compId),
        };
      }
    }

    // Create leaders if required.
    const leadersDoc = leaderboardRef
      .collection('leaderboard_data')
      .doc('leaders');
    if ((await leadersDoc.get()).exists === false) {
      log(
        `Adding empty leaders doc [type: ${leaderboardConfig.type}] [vendor: ${vendor}] [key: ${leaderboardConfig.key}].\n`,
      );
      await leaderboardRef
        .collection('leaderboard_data')
        .doc('leaders')
        .set(
          assertType<TLeaders>({
            leaders: [],
          }),
        );
    }

    await uploadAssetsForLeaderboard(
      leaderboardToUpload,
      files,
      config,
      log,
      true,
      prefix,
    );
    log(`Uploading leaderboard [${leaderboardRef.id}]\n\n`);
    await leaderboardRef.set(leaderboardToUpload);
    leaderboardIds.push(leaderboardRef.id);
  }
  if (!inGameLeaderboard) {
    throw new Error(
      "There should be exactly 1 ingame leaderboard associated with a comp, found '0'",
    );
  }
  return leaderboardIds;
};

export const uploadAssetsForLeaderboard = async (
  leaderboardConfig: TLocalLeaderboard,
  files: File[],
  config: {protocol: string; cdnDomain: string},
  log: (message: string) => void,
  required: boolean = true,
  prefix: string = '',
) => {
  const fileName = getLocalLeaderboardImageFileName(leaderboardConfig.key);
  const checkFileName = (file: File, name: string): boolean => {
    const namesToSearch = [`${prefix}${name}`, name];
    return namesToSearch.some((n) => fileSearch(file, n));
  };
  const fileIndex = files.findIndex((f) => checkFileName(f, fileName));
  if (fileIndex === -1 && required) {
    throw new Error(
      `Cannot find an asset file that matches '${[
        `${prefix}${fileName}`,
        fileName,
      ].join(', ')}'`,
    );
  }
  if (fileIndex !== -1) {
    const file = files[fileIndex];
    files.splice(fileIndex, 1);
    const newMediaEntry = await uploadAssetForDocument(
      config,
      file,
      (_progress, message) => log(message),
    );
    leaderboardConfig.media = {
      ...leaderboardConfig.media,
      ...newMediaEntry,
    };
  }

  for (let j = 0; j < leaderboardConfig.prizes.length; j++) {
    const prize = leaderboardConfig.prizes[j];
    const fileName = getLocalLeaderboardPrizeImageFileName(
      leaderboardConfig.key,
      j,
    );
    const fileIndex = files.findIndex((f) => checkFileName(f, fileName));
    if (fileIndex === -1 && required) {
      throw new Error(
        `Cannot find an asset file that matches '${[
          `${prefix}${fileName}`,
          fileName,
        ].join(', ')}'`,
      );
    }
    if (fileIndex !== -1) {
      const file = files[fileIndex];
      files.splice(fileIndex, 1);

      const newMediaEntry = await uploadAssetForDocument(
        config,
        file,
        (_progress, message) => log(message),
      );
      prize.media = {
        ...prize.media,
        ...newMediaEntry,
      };
    }
  }
};
